import React, { useMemo, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.core.css';
import 'react-quill-new/dist/quill.bubble.css';
import '@staty/shared/styles/quillContent.css';
import { stripQuillNbsp } from '@/utils/quillHtml';

/**
 * In-place rich text editing on the canvas: Quill 2's BUBBLE theme — no
 * chrome at rest, a floating toolbar over the current selection. This is the
 * Squarespace interaction, and because the editable surface is a `.ql-editor`
 * the shared quillContent.css typography applies while typing, exactly as it
 * will render publicly.
 *
 * Toolbar is the PageEditor4 baseline minus image/video/font/size: images and
 * embeds are their own block types now, and font/size pickers fight the
 * shared typography contract the last two parity fixes established.
 */

const TOOLBAR = [
  [{ header: 1 }, { header: 2 }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }],
  ['blockquote'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['link'],
  ['clean'],
];

interface Props {
  blockId: string;
  html: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

export default function RichtextBlockEditor({ blockId, html, onChange, placeholder }: Props) {
  // Uncontrolled after mount: feeding `value` back on every keystroke makes
  // Quill re-set its contents and drop the caret. The draft store is the
  // source of truth for SAVING; Quill owns the DOM while editing. Keyed by
  // block id (parent), so switching posts remounts with the right html.
  const initialHtml = useRef(html);
  const modules = useMemo(() => ({ toolbar: TOOLBAR }), []);

  return (
    <div className="canvas-richtext" data-block-id={blockId}>
      <ReactQuill
        theme="bubble"
        defaultValue={initialHtml.current}
        // Quill glues words with &nbsp; in some input paths. A non-breaking
        // space directly before a word blocks wrapping — publicly the line
        // then only breaks MID-word (word-wrap: break-word) or, inside a
        // column, blows the grid track to the line's full width. Shared fix
        // (also used by WaiverAdmin): utils/quillHtml.ts.
        onChange={(content: string) => onChange(stripQuillNbsp(content))}
        modules={modules}
        placeholder={placeholder || 'Write something…'}
      />
    </div>
  );
}
